if (!customElements.get('lp-slider')) {
  customElements.define(
    'lp-slider',
    class LpSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.lp-slider__track');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.prev = this.querySelector('[data-lp-prev]');
        this.next = this.querySelector('[data-lp-next]');
        this.dotsWrap = this.querySelector('[data-lp-dots]');

        this.prev?.addEventListener('click', () => this.go(this.index() - 1));
        this.next?.addEventListener('click', () => this.go(this.index() + 1));

        if (this.dotsWrap) {
          this.dotsWrap.innerHTML = '';
          this.dots = this.slides.map((_, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'lp-slider__dot';
            dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
            dot.addEventListener('click', () => this.go(i));
            this.dotsWrap.appendChild(dot);
            return dot;
          });
        }

        this.onScroll = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.update());
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onScroll);
      }

      step() {
        if (this.slides.length > 1) return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
        return this.track.clientWidth;
      }

      index() {
        return Math.round(this.track.scrollLeft / this.step());
      }

      maxIndex() {
        return Math.max(0, Math.round((this.track.scrollWidth - this.track.clientWidth) / this.step()));
      }

      go(target) {
        const index = Math.max(0, Math.min(target, this.maxIndex()));
        this.track.scrollTo({ left: index * this.step(), behavior: 'smooth' });
      }

      update() {
        const index = this.index();
        const max = this.maxIndex();
        if (this.prev) this.prev.disabled = index <= 0;
        if (this.next) this.next.disabled = index >= max;
        this.dots?.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.hidden = i > max;
        });
      }
    }
  );
}

if (!customElements.get('lp-gallery')) {
  customElements.define(
    'lp-gallery',
    // Slides sit side by side in a scroll-snap track, so touch and trackpad
    // swipes are native. Mouse users can drag, use the arrows or the keyboard.
    class LpGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-lp-track]');
        if (!this.track) return;
        this.slides = Array.from(this.track.querySelectorAll('[data-lp-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-lp-thumb]'));
        this.thumbsWrap = this.thumbs[0]?.parentElement;
        this.prev = this.querySelector('[data-lp-gallery-prev]');
        this.next = this.querySelector('[data-lp-gallery-next]');
        this.current = 0;

        this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.show(i)));
        this.prev?.addEventListener('click', () => this.show(this.current - 1));
        this.next?.addEventListener('click', () => this.show(this.current + 1));
        this.track.addEventListener('scroll', () => this.sync(), { passive: true });
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.show(this.current - 1);
          if (event.key === 'ArrowRight') this.show(this.current + 1);
        });
        this.enableMouseDrag();

        // Off-screen slides are lazy; fetch them once the page has loaded so a
        // swipe never lands on an empty frame.
        const loadAll = () => this.track.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        if (document.readyState === 'complete') loadAll();
        else window.addEventListener('load', loadAll, { once: true });

        this.mark(0);
      }

      index() {
        return Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
      }

      show(target) {
        const index = Math.max(0, Math.min(target, this.slides.length - 1));
        // While the smooth scroll passes the slides in between, keep the
        // thumbs on the target instead of flickering along.
        this.target = index;
        clearTimeout(this.targetTimer);
        this.targetTimer = setTimeout(() => (this.target = null), 800);
        this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
        this.mark(index);
      }

      sync() {
        if (this.dragging) return;
        const index = this.index();
        if (this.target != null) {
          if (index === this.target) this.target = null;
          return;
        }
        if (index !== this.current) this.mark(index);
      }

      mark(index) {
        this.current = index;
        this.thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === index);
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        if (this.prev) this.prev.disabled = index <= 0;
        if (this.next) this.next.disabled = index >= this.slides.length - 1;

        // Keep the active thumb in view without scrolling the page.
        const thumb = this.thumbs[index];
        if (thumb && this.thumbsWrap) {
          const wrap = this.thumbsWrap;
          const left = thumb.offsetLeft - wrap.offsetLeft;
          if (left < wrap.scrollLeft || left + thumb.offsetWidth > wrap.scrollLeft + wrap.clientWidth) {
            wrap.scrollTo({ left: left - (wrap.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
          }
        }
      }

      enableMouseDrag() {
        if (this.slides.length < 2) return;
        let startX = 0;
        let startScroll = 0;
        let moved = 0;

        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          this.dragging = true;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
          moved = 0;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture(event.pointerId);
        });

        this.track.addEventListener('pointermove', (event) => {
          if (!this.dragging) return;
          moved = event.clientX - startX;
          this.track.scrollLeft = startScroll - moved;
        });

        const end = () => {
          if (!this.dragging) return;
          this.dragging = false;
          // A short flick is enough to change slide; otherwise snap back.
          let target = this.current;
          if (moved < -40) target = this.current + 1;
          if (moved > 40) target = this.current - 1;
          this.show(target);
          // Snapping stays off until the smooth scroll has settled, or the
          // browser jumps to the nearest slide straight away.
          clearTimeout(this.snapTimer);
          this.snapTimer = setTimeout(() => this.track.classList.remove('is-dragging'), 450);
        };
        this.track.addEventListener('pointerup', end);
        this.track.addEventListener('pointercancel', end);
      }
    }
  );
}

if (!customElements.get('lp-buy-box')) {
  customElements.define(
    'lp-buy-box',
    class LpBuyBox extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-lp-variants]');
        this.variants = data ? JSON.parse(data.textContent) : [];
        this.currency = this.dataset.currency;
        this.locale = this.dataset.locale || document.documentElement.lang || 'en';
        this.subscribeDiscount = parseFloat(this.dataset.subscribeDiscount || '0');
        this.idInput = this.querySelector('input[name="id"]');
        this.qtyInput = this.querySelector('input[name="quantity"]');
        this.planInput = this.querySelector('input[name="selling_plan"]');
        this.variantSelect = this.querySelector('[data-lp-variant-select]');
        this.bundles = Array.from(this.querySelectorAll('[data-lp-bundle]'));
        this.purchaseOptions = Array.from(this.querySelectorAll('[data-lp-purchase]'));

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-lp-bundle-input], [data-lp-purchase-input], [data-lp-variant-select]')) {
            this.render();
          }
        });

        this.render();
        this.renderDelivery();
      }

      currentVariant() {
        const id = Number(this.variantSelect ? this.variantSelect.value : this.idInput?.value);
        return this.variants.find((variant) => variant.id === id) || this.variants[0];
      }

      format(cents) {
        try {
          return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(cents / 100);
        } catch (error) {
          return (cents / 100).toFixed(2);
        }
      }

      isSubscription() {
        return this.purchaseOptions.some(
          (option) => option.dataset.lpPurchase === 'subscribe' && option.querySelector('input').checked
        );
      }

      render() {
        const variant = this.currentVariant();
        if (!variant || !this.idInput) return;

        this.idInput.value = variant.id;
        const subscription = this.isSubscription();
        if (this.planInput) this.planInput.disabled = !subscription;

        const multiplier = subscription ? 1 - this.subscribeDiscount / 100 : 1;
        const unitPrice = variant.price * multiplier;
        const unitCompare = Math.max(variant.compare_at_price || 0, variant.price);
        let selected = null;

        this.bundles.forEach((bundle) => {
          const input = bundle.querySelector('input');
          const quantity = Number(bundle.dataset.qty);
          const paid = Math.min(Number(bundle.dataset.paid), quantity);
          const price = Math.round(unitPrice * paid);
          const compare = Math.round(unitCompare * quantity);
          const saving = Math.max(compare - price, 0);

          bundle.querySelector('[data-lp-price]').textContent = this.format(price);
          const was = bundle.querySelector('[data-lp-was]');
          if (was) {
            was.hidden = saving === 0;
            was.textContent = this.format(compare);
          }
          const pct = bundle.querySelector('[data-lp-pct]');
          if (pct) {
            pct.hidden = saving === 0;
            pct.querySelector('[data-lp-pct-value]').textContent = compare ? Math.floor((saving * 100) / compare) : 0;
          }
          const saveLine = bundle.querySelector('[data-lp-save-line]');
          if (saveLine) {
            saveLine.hidden = saving === 0;
            saveLine.querySelector('[data-lp-save]').textContent = this.format(saving);
          }
          bundle.classList.toggle('is-selected', input.checked);
          if (input.checked) selected = { quantity, price, compare };
        });

        this.purchaseOptions.forEach((option) =>
          option.classList.toggle('is-selected', option.querySelector('input').checked)
        );

        if (!selected) selected = { quantity: 1, price: Math.round(unitPrice), compare: unitCompare };
        if (this.qtyInput) this.qtyInput.value = selected.quantity;

        this.querySelectorAll('[data-lp-atc-price]').forEach((el) => (el.textContent = this.format(selected.price)));
        this.querySelectorAll('[data-lp-atc-compare]').forEach(
          (el) => (el.textContent = selected.compare > selected.price ? this.format(selected.compare) : '')
        );

        const button = this.querySelector('[data-lp-atc]');
        if (button) {
          button.disabled = !variant.available;
          const text = button.querySelector('[data-lp-atc-text]');
          const priceWrap = button.querySelector('[data-lp-atc-pricewrap]');
          if (text) text.textContent = variant.available ? button.dataset.labelAvailable : button.dataset.labelSoldOut;
          if (priceWrap) priceWrap.hidden = !variant.available;
        }
      }

      renderDelivery() {
        const el = this.querySelector('[data-lp-delivery]');
        if (!el) return;
        const max = Number(el.dataset.days || 5);
        const min = Number(el.dataset.daysMin || 0);

        if (min > 0 && min < max) {
          const month = { month: 'short' };
          el.textContent = `${this.formatDay(this.addBusinessDays(min), month)} – ${this.formatDay(this.addBusinessDays(max), month)}`;
        } else {
          el.textContent = this.formatDay(this.addBusinessDays(max), { month: 'long' });
        }
      }

      addBusinessDays(count) {
        const date = new Date();
        let added = 0;
        while (added < count) {
          date.setDate(date.getDate() + 1);
          if (date.getDay() !== 0 && date.getDay() !== 6) added += 1;
        }
        return date;
      }

      formatDay(date, monthFormat) {
        const day = date.getDate();
        let suffix = 'th';
        if (day < 11 || day > 13) suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
        return `${date.toLocaleDateString('en-US', monthFormat)} ${day}${suffix}`;
      }
    }
  );
}

if (!customElements.get('lp-sticky-atc')) {
  customElements.define(
    'lp-sticky-atc',
    class LpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.target = document.getElementById(this.dataset.target);
        if (!this.target) return;

        this.onScroll = () => {
          const passed = this.target.getBoundingClientRect().bottom < 0;
          if (passed === this.passed) return;
          this.passed = passed;
          this.classList.toggle('is-visible', passed);
          document.body.classList.toggle('lp-sticky-active', passed);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.onScroll();

        this.querySelector('[data-lp-sticky-btn]')?.addEventListener('click', () => {
          document.getElementById(this.dataset.scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        document.body.classList.remove('lp-sticky-active');
      }
    }
  );
}
